import { useEffect, useMemo, useState } from "react";
import UnifiedCalendar from "../common/Calendar";
import {
  addDays,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
} from "date-fns";
import { toast } from "react-toastify";
import CustomButton from "../common/CustomButton";
import { useForm, useFieldArray } from "react-hook-form";
import { FiChevronDown, FiFilter, FiTrash2, FiXCircle } from "react-icons/fi";
import TogglePill from "../common/CustomToggleFilter";

/* =========================
   CONSTANTS
   ========================= */
const WEEK_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const inputBase =
  "h-10 px-3 rounded-lg border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-toucanTeal focus:border-toucanTeal";

const timeInput =
  "h-10 px-3 rounded-lg border border-toucanTeal/40 text-sm focus:outline-none focus:ring-2 focus:ring-toucanTeal bg-white";

/* =========================
   COMPONENT
   ========================= */
export default function Step2Availability({
  defaultValues,
  onNext,
  onBack,
  isEditMode,
}) {
  /* =====================================================
     SECTION 1 – DEFAULT DAILY SCHEDULES (DOCS COMPLIANT)
     ===================================================== */

  useEffect(() => {
    if (!defaultValues) return;

    /* 1️⃣ MAP SCHEDULES */
    const mappedSchedules = defaultValues.schedules || [];

    setSchedules(mappedSchedules);

    const activeSchedule =
      mappedSchedules.find((s) => s.isActive) || mappedSchedules[0];

    if (activeSchedule) {
      setEditingSchedule(activeSchedule);
      setSelectedScheduleId(activeSchedule.id);
    }

    /* 2️⃣ ESCALATION RULES */
    if (defaultValues.escalationRules?.length) {
      const sortedRules = defaultValues.escalationRules
        .map((r) => ({
          minGuests: r.minGuests,
          maxGuests: r.maxGuests,
          duration: r.durationMinutes,
        }))
        .sort((a, b) => Number(a.minGuests) - Number(b.minGuests));

      setEscalationRules(sortedRules);
    }

    /* 3️⃣ CALENDAR AVAILABILITY */
    if (
      defaultValues.availabilityStartDate &&
      defaultValues.availabilityEndDate &&
      activeSchedule
    ) {
      const start = startOfDay(new Date(defaultValues.availabilityStartDate));
      const end = startOfDay(new Date(defaultValues.availabilityEndDate));

      let day = start;
      const availability = {};

      while (day <= end) {
        const key = format(day, "yyyy-MM-dd");
        const weekday = format(day, "EEE");

        if (activeSchedule.days.includes(weekday)) {
          availability[key] = { available: true };
        }

        day = addDays(day, 1);
      }

      setCalendarAvailability(availability);
      setRangeStart(start);
      setRangeEnd(end);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (!defaultValues?.availabilityOverrides) return;

    setCalendarAvailability((prev) => {
      const updated = { ...prev };

      defaultValues.availabilityOverrides.forEach((o) => {
        const dateKey = o.date.split("T")[0];

        if (o.type === "FullDayClosed") {
          updated[dateKey] = {
            available: false,
            overrideType: "FULL_CLOSE",
          };
        }

        if (o.type === "CustomWindow") {
          updated[dateKey] = {
            available: true,
            overrideType: "CUSTOM_HOURS",
            customHours: {
              start: o.startTime,
              end: o.endTime,
            },
          };
        }

        if (o.type === "BlockedRange") {
          updated[dateKey] = {
            available: true,
            overrideType: "CLOSURE_WINDOWS",
            closures: [
              {
                start: o.startTime?.slice(0, 5),
                end: o.endTime?.slice(0, 5),
              },
            ],
          };
        }
      });

      return updated;
    });
  }, [defaultValues]);

  useEffect(() => {
    if (!defaultValues) return;

    // specificStartTime is already boolean
    setSpecificStartRequired(defaultValues.specificStartRequired);

    if (defaultValues.interval) {
      setStartInterval(defaultValues.startInterval);
    } else {
      setStartInterval("");
    }
  }, [defaultValues]);

  const [openOverridePanels, setOpenOverridePanels] = useState([]);
  const [intervalDropdownOpen, setIntervalDropdownOpen] = useState(false);

  const intervalOptions = [
    { label: "15 minutes", value: 15 },
    { label: "30 minutes", value: 30 },
    { label: "45 minutes", value: 45 },
    { label: "60 minutes", value: 60 },
    { label: "90 minutes", value: 90 },
    { label: "120 minutes", value: 120 },
  ];

  useEffect(() => {
    if (!defaultValues) return;

    setSpecificStartRequired(defaultValues.specificStartTime);
    setStartInterval(defaultValues.interval);
  }, [defaultValues]);

  const [schedules, setSchedules] = useState([]);
  const [selectedScheduleId, setSelectedScheduleId] = useState(
    schedules.find((s) => s.isActive)?.id,
  );

  const [calendarMode, setCalendarMode] = useState("idle");
  // idle | range | override
  const [escalationRules, setEscalationRules] = useState([]);
  const [openEscalationRows, setOpenEscalationRows] = useState([]);

  const handleAddEscalationRule = () => {
    setEscalationRules((prev) => [
      ...prev,
      {
        minGuests: "",
        maxGuests: "",
        duration: "",
        action: "REQUIRES_APPROVAL",
      },
    ]);
  };

  useEffect(() => {
    if (defaultValues) {
      const isFixed = !!defaultValues?.specificStartTime;
      setSpecificStartRequired(isFixed);
    }
  }, [defaultValues]);

  const validateEscalationRules = () => {
    for (let i = 0; i < escalationRules.length; i++) {
      const r = escalationRules[i];
      const prev = escalationRules[i - 1];

      // empty check
      if (!r.minGuests || !r.maxGuests || !r.duration) {
        toast.error("Min, Max and Duration are required");
        return false;
      }
      // NEW GLOBAL MAX CHECK

      if (!isPrivate && Number(r.maxGuests) > maxGuestCount) {
        toast.error(`Max guests cannot exceed ${maxGuestCount}`);
        return false;
      }

      // min < max
      if (Number(r.minGuests) >= Number(r.maxGuests)) {
        toast.error("Max guests must be greater than Min guests");
        return false;
      }

      // previous rule comparison
      if (prev) {
        if (Number(r.minGuests) <= Number(prev.maxGuests)) {
          toast.error(
            `Min guests must be greater than previous max (${prev.maxGuests})`,
          );
          return false;
        }

        if (Number(r.duration) <= Number(prev.duration)) {
          toast.error(
            `Duration must be greater than previous duration (${prev.duration})`,
          );
          return false;
        }
      }
    }

    return true;
  };

  const handleRemoveEscalationRule = (index) => {
    setEscalationRules((prev) => prev.filter((_, i) => i !== index));
  };

  const activeSchedule = schedules.find((s) => s.isActive);

  const [editingSchedule, setEditingSchedule] = useState(null);

  useEffect(() => {
    if (!selectedScheduleId) return;

    const selected = schedules.find((s) => s.id === selectedScheduleId);

    if (selected) {
      setEditingSchedule(selected);
    }
  }, [selectedScheduleId, schedules]);

  const [lastClickedDate, setLastClickedDate] = useState(null);

  const handleSelectSchedule = (id) => {
    setSchedules((prev) =>
      prev.map((s) => ({
        ...s,
        isActive: s.id === id,
      })),
    );

    setSelectedScheduleId(id);
  };

  const selectedSchedule =
    schedules.find((s) => s.id === selectedScheduleId) || activeSchedule;

  const handleNewSchedule = () => {
    const id = Date.now().toString();

    const newSchedule = {
      id,
      name: "New Schedule",
      days: [],
      openTime: "09:00",
      closeTime: "17:00",
      isActive: false,
    };

    setSchedules((p) => [...p, newSchedule]);
    setEditingSchedule(newSchedule);
  };

  const handleSaveSchedule = () => {
    if (!editingSchedule.name.trim()) {
      toast.error("Schedule name is required");
      return;
    }

    if (!editingSchedule.days.length) {
      toast.error("Select at least one open day");
      return;
    }

    if (editingSchedule.closeTime <= editingSchedule.openTime) {
      toast.error("Closing time must be after opening time");
      return;
    }

    setSchedules((prev) =>
      prev.map((s) => (s.id === editingSchedule.id ? editingSchedule : s)),
    );

    toast.success("Schedule saved");
  };

  /* =====================================================
     VALUES USED BY EXISTING LOGIC (UNCHANGED)
     ===================================================== */
  const openDays = selectedSchedule?.days;
  const openTime = selectedSchedule?.openTime;
  const closeTime = selectedSchedule?.closeTime;

  const handleDeleteSchedule = () => {
    if (schedules.length === 1) {
      toast.error("At least one schedule is required");
      return;
    }

    const remaining = schedules.filter((s) => s.id !== editingSchedule.id);

    const nextActive = remaining[0];

    setSchedules(
      remaining.map((s, i) => ({
        ...s,
        isActive: i === 0,
      })),
    );

    setEditingSchedule({ ...nextActive });
    setSelectedScheduleId(nextActive.id);

    toast.success("Schedule deleted");
  };

  /* =========================
     EXISTING STATES (UNCHANGED)
     ========================= */
  const today = startOfDay(new Date());

  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [calendarAvailability, setCalendarAvailability] = useState({});
  const [visibleMonth, setVisibleMonth] = useState(startOfMonth(new Date()));

  const [overrideType, setOverrideType] = useState(null);
  const [customStart, setCustomStart] = useState("09:00");
  const [customEnd, setCustomEnd] = useState("17:00");
  const [closures, setClosures] = useState([]);
  const [specificStartRequired, setSpecificStartRequired] = useState(false);
  const [startInterval, setStartInterval] = useState("");

  useEffect(() => {
    if (!defaultValues) return;

    setSpecificStartRequired(defaultValues?.specificStartRequired);

    setStartInterval(defaultValues?.startInterval || "");
  }, [defaultValues]);

  const keepOverridesOnly = (availability) => {
    const result = {};
    Object.entries(availability).forEach(([key, value]) => {
      if (value && value.overrideType) {
        result[key] = value; // sirf overrides rehengi
      }
    });
    return result;
  };

  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      durationRules: [],
    },
  });

  const {
    fields: durationFields,
    append: addDuration,
    remove: removeDuration,
  } = useFieldArray({
    control,
    name: "durationRules",
  });
  const durationRules = watch("durationRules");
  const [openRows, setOpenRows] = useState([]);

  const toggleRow = (index) => {
    setOpenRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const existingOverride =
    selectedDate && calendarAvailability[selectedDate]
      ? calendarAvailability[selectedDate]
      : null;

  /* =========================
     AUTO POPULATE (UNCHANGED)
     ========================= */
  const handleAutoPopulateMonth = () => {
    if (!selectedSchedule || !selectedSchedule.days?.length) {
      toast.error("Please select and configure a schedule first");
      return;
    }

    if (!selectedSchedule.openTime || !selectedSchedule.closeTime) {
      toast.error("Please set opening and closing time");
      return;
    }
    const base = keepOverridesOnly(calendarAvailability);
    let updated = { ...base };

    let day = startOfMonth(visibleMonth);
    const end = endOfMonth(visibleMonth);

    let first = null;
    let last = null;

    while (day <= end) {
      const key = format(day, "yyyy-MM-dd");
      const weekday = format(day, "EEE");

      if (day >= today && openDays.includes(weekday)) {
        if (!updated[key]?.overrideType) {
          updated[key] = { available: true };
          if (!first) first = day;
          last = day;
        }
      }

      day = addDays(day, 1);
    }

    setCalendarAvailability(updated);

    // ✅ RANGE SET (FOR STYLING + OVERRIDE MODE)
    setRangeStart(first);
    setRangeEnd(last);

    toast.success("Schedule applied for this month");

    setSelectedDate(null);
    setOverrideType(null);
    setClosures([]);
  };

  const now = new Date();

  const todayStr =
    now.getFullYear() +
    "-" +
    String(now.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(now.getDate()).padStart(2, "0");

  const handleAutoPopulateYear = () => {
    if (!selectedSchedule) {
      toast.error("Please select a schedule first");
      return;
    }

    if (
      !Array.isArray(selectedSchedule.days) ||
      selectedSchedule.days.length === 0
    ) {
      toast.error("Please select at least one open day");
      return;
    }

    if (!selectedSchedule.openTime || !selectedSchedule.closeTime) {
      toast.error("Please set opening and closing time");
      return;
    }

    const base = keepOverridesOnly(calendarAvailability);
    let updated = { ...base };

    const yearStart = startOfYear(visibleMonth);
    const yearEnd = endOfYear(visibleMonth);

    let day = yearStart < today ? today : yearStart;

    let first = null;
    let last = null;

    while (day <= yearEnd) {
      const key = format(day, "yyyy-MM-dd");
      const weekday = format(day, "EEE");

      if (openDays.includes(weekday)) {
        if (!updated[key]?.overrideType) {
          updated[key] = { available: true };
          if (!first) first = day;
          last = day;
        }
      }

      day = addDays(day, 1);
    }

    setCalendarAvailability(updated);

    // ✅ RANGE SET (FOR STYLING + OVERRIDE MODE)
    setRangeStart(first);
    setRangeEnd(last);

    toast.success("Schedule applied for this year");

    setSelectedDate(null);
    setOverrideType(null);
    setClosures([]);
  };

  /* =========================
     APPLY RANGE
     ========================= */
  const handleApplyToDateRange = () => {
    if (!rangeStart || !rangeEnd) {
      toast.error("Please select start and end date");
      return;
    }

    const base = keepOverridesOnly(calendarAvailability);
    let updated = { ...base };

    let day = startOfDay(new Date(rangeStart));

    while (day <= rangeEnd) {
      const key = format(day, "yyyy-MM-dd");
      const weekday = format(day, "EEE");

      if (day >= today && openDays.includes(weekday)) {
        if (!updated[key]?.overrideType) {
          updated[key] = { ...(updated[key] || {}), available: true };
        }
      }

      day = addDays(day, 1);
    }

    setCalendarAvailability(updated);

    // ✅ SUCCESS TOAST
    toast.success(
      `Schedule applied from ${format(rangeStart, "MMM dd")} to ${format(
        rangeEnd,
        "MMM dd",
      )}`,
    );

    // ❌ range ko reset mat karo
    // sirf override related state clear karo
    setSelectedDate(null);
    setOverrideType(null);
    setClosures([]);
  };

  const resetSelectionState = () => {
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedDate(null);
    setOverrideType(null);
    setClosures([]);
  };

  /* =========================
     OVERRIDE HELPERS (UNCHANGED)
     ========================= */
  const addMinutes = (time, mins) => {
    const [h, m] = time.split(":").map(Number);
    const d = new Date();
    d.setHours(h, m + mins, 0);
    return d.toTimeString().slice(0, 5);
  };

  const handleAddWindow = () => {
    const newWindow = {
      start: openTime,
      end: addMinutes(openTime, 60),
    };

    // ❌ basic sanity check
    if (newWindow.end <= newWindow.start) {
      toast.error("End time must be after start time");
      return;
    }

    // ❌ overlap / duplicate check
    if (hasOverlappingWindow(closures, newWindow)) {
      toast.error("This time window overlaps with an existing one");
      return;
    }

    // ✅ safe to add
    setClosures((prev) => [...prev, newWindow]);
  };
  const hasOverlappingWindow = (windows, newWindow) => {
    return windows.some((w) => {
      return newWindow.start < w.end && newWindow.end > w.start;
    });
  };
  const handleClearSchedule = () => {
    setEditingSchedule((p) => ({
      ...p,
      name: "",
      days: [],
      openTime: "",
      closeTime: "",
    }));

    toast.success("Schedule cleared");
  };

  const handleSaveOverride = () => {
    if (!overrideType || !selectedDate) return;
    if (overrideType === "CLOSURE_WINDOWS") {
      for (let i = 0; i < closures.length; i++) {
        const current = closures[i];

        if (!current.start || !current.end || current.end <= current.start) {
          toast.error("Invalid closure window time");
          return;
        }

        const rest = closures.filter((_, idx) => idx !== i);

        if (hasOverlappingWindow(rest, current)) {
          toast.error("Closure windows cannot overlap");
          return;
        }
      }
    }

    if (overrideType === "CUSTOM_HOURS" && customEnd <= customStart) {
      toast.error("End time must be after start time");
      return;
    }

    if (
      overrideType === "CLOSURE_WINDOWS" &&
      closures.some((w) => !w.start || !w.end || w.end <= w.start)
    ) {
      toast.error("Invalid closure window");
      return;
    }

    const updated = { ...calendarAvailability };

    if (overrideType === "FULL_CLOSE") {
      updated[selectedDate] = { available: false, overrideType };
    }

    if (overrideType === "CUSTOM_HOURS") {
      updated[selectedDate] = {
        available: true,
        overrideType,
        customHours: { start: customStart, end: customEnd },
      };
    }

    if (overrideType === "CLOSURE_WINDOWS") {
      updated[selectedDate] = {
        available: true,
        overrideType,
        closures,
      };
    }

    setCalendarAvailability(updated);
    toast.success("Override saved");
  };

  const handleRemoveOverride = () => {
    const updated = { ...calendarAvailability };
    delete updated[selectedDate];
    setCalendarAvailability(updated);
    setOverrideType(null);
    setClosures([]);
    toast.success("Override removed");
  };

  const handleCalendarRangeSelect = (dateObj) => {
    const date = new Date(dateObj);

    if (!rangeStart) {
      setRangeStart(date);
      setRangeEnd(null);
      return;
    }

    if (rangeStart && !rangeEnd) {
      if (date < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(date);
      } else {
        setRangeEnd(date);
      }
      return;
    }

    // third click = reset
    setRangeStart(date);
    setRangeEnd(null);
  };

  const handleCalendarClick = (dateObj) => {
    const date = startOfDay(new Date(dateObj));
    if (date < today) return;

    const key = format(date, "yyyy-MM-dd");

    // ✅ CASE 1: RANGE COMPLETE HAI
    if (rangeStart && rangeEnd) {
      const start = startOfDay(rangeStart).getTime();
      const end = startOfDay(rangeEnd).getTime();
      const clicked = date.getTime();

      const min = Math.min(start, end);
      const max = Math.max(start, end);

      // 🔥 CLICK INSIDE RANGE → OVERRIDE MODE
      if (clicked >= min && clicked <= max) {
        setSelectedDate(key);

        const existing = calendarAvailability[key];

        if (existing?.overrideType) {
          setOverrideType(existing.overrideType);

          if (existing.overrideType === "CUSTOM_HOURS") {
            setCustomStart(existing.customHours?.start || "09:00");
            setCustomEnd(existing.customHours?.end || "17:00");
          }

          if (existing.overrideType === "CLOSURE_WINDOWS") {
            setClosures(existing.closures || []);
          }
        } else {
          setOverrideType(null);
          setClosures([]);
        }

        return;
      }

      // 🔥 CLICK OUTSIDE RANGE → RESET RANGE & START NEW
      setRangeStart(date);
      setRangeEnd(null);
      setSelectedDate(null);
      setOverrideType(null);
      setClosures([]);
      return;
    }

    // ✅ FIRST CLICK
    if (!rangeStart) {
      setRangeStart(date);
      setRangeEnd(null);
      setSelectedDate(null);
      return;
    }

    // ✅ SECOND CLICK
    if (rangeStart && !rangeEnd) {
      if (date < rangeStart) {
        setRangeEnd(rangeStart);
        setRangeStart(date);
      } else {
        setRangeEnd(date);
      }
      setSelectedDate(null);
      return;
    }
  };

  const handleClearCalendar = () => {
    // ✅ sab kuch hata do – availability + overrides
    setCalendarAvailability({});

    // 🔄 UI reset
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedDate(null);
    setOverrideType(null);
    setClosures([]);
    setLastClickedDate(null);

    toast.success("All calendar availability and overrides cleared");
  };

  const [isScheduleListOpen, setIsScheduleListOpen] = useState(true);
  const visibleSchedules = isScheduleListOpen
    ? schedules
    : schedules.slice(0, 3);

  const isScheduleValid =
    !!selectedSchedule &&
    selectedSchedule.name?.trim() &&
    selectedSchedule.days?.length > 0 &&
    selectedSchedule.openTime &&
    selectedSchedule.closeTime &&
    selectedSchedule.closeTime > selectedSchedule.openTime;

  const [scheduleDropdownOpen, setScheduleDropdownOpen] = useState(false);

  const maxGuestCount = Number(defaultValues?.maxGuestCount) || 0;
  const isPrivate = defaultValues?.isPrivate === true;

  useEffect(() => {
    if (!selectedDate) return;

    const existing = calendarAvailability[selectedDate];

    if (!existing?.overrideType) {
      setOverrideType(null);
      setClosures([]);
      return;
    }

    setOverrideType(existing.overrideType);

    if (existing.overrideType === "CUSTOM_HOURS") {
      setCustomStart(existing.customHours?.start || "09:00");
      setCustomEnd(existing.customHours?.end || "17:00");
    }

    if (existing.overrideType === "CLOSURE_WINDOWS") {
      setClosures(existing.closures || []);
    }
  }, [selectedDate]);

  const generateStartTimes = (open, close, interval) => {
    if (!open || !close || !interval) return [];

    const result = [];

    const [openH, openM] = open.split(":").map(Number);
    const [closeH, closeM] = close.split(":").map(Number);

    const current = new Date();
    current.setHours(openH, openM, 0, 0);

    const end = new Date();
    end.setHours(closeH, closeM, 0, 0);

    while (current < end) {
      const formatted = current.toLocaleTimeString([], {
        hour: "numeric",
        minute: "2-digit",
        hour12: true, // 🔥 THIS enables AM/PM
      });

      result.push(formatted);

      current.setMinutes(current.getMinutes() + Number(interval));
    }

    return result;
  };

  const previewStartTimes = useMemo(() => {
    if (
      !specificStartRequired ||
      !startInterval ||
      !selectedSchedule?.openTime ||
      !selectedSchedule?.closeTime
    ) {
      return [];
    }

    return generateStartTimes(
      selectedSchedule.openTime,
      selectedSchedule.closeTime,
      startInterval,
    );
  }, [
    specificStartRequired,
    startInterval,
    selectedSchedule?.openTime,
    selectedSchedule?.closeTime,
  ]);
  /* =========================
     RENDER
     ========================= */
  return (
    <div className="bg-white rounded-xl border p-6 sm:p-8">
      {/* ================= SECTION 1 ================= */}

      <h3 className="text-[16px] font-semibold mb-1">
        Default Daily Schedules
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Define how a normal operating day works. Create multiple schedules and
        apply one to the calendar.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5 md:gap-6 mb-8">
        {/* ================= LEFT PANEL ================= */}
        <div className="border rounded-xl overflow-hidden bg-white">
          {/* HEADER */}
          <div
            className="flex items-center justify-between px-4 py-3 cursor-pointer bg-gray-50"
            onClick={() => setIsScheduleListOpen((p) => !p)}
          >
            <span className="text-sm font-medium text-gray-700">Schedules</span>

            <FiChevronDown
              size={16}
              className={`text-gray-500 transition-transform duration-200 ${
                isScheduleListOpen ? "rotate-180" : ""
              }`}
            />
          </div>

          {/* BODY */}
          <div className="p-4 space-y-3">
            {visibleSchedules.map((s) => (
              <button
                key={s.id}
                onClick={() => handleSelectSchedule(s.id)}
                className={`w-full text-left p-3 rounded-lg border transition ${
                  s.isActive
                    ? "border-toucanTeal bg-toucanTeal/10"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="font-medium text-sm">{s.name}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {s.days?.length ? s.days.join(", ") : "No days"} ·
                  {s.openTime && s.closeTime
                    ? ` ${s.openTime}–${s.closeTime}`
                    : " No time"}
                </div>
              </button>
            ))}

            {!isScheduleListOpen && schedules.length > 3 && (
              <div className="text-xs text-gray-400 text-center">
                +{schedules.length - 3} more schedules
              </div>
            )}

            <CustomButton
              variant="secondary"
              fullWidth
              onClick={handleNewSchedule}
              className="mt-2"
            >
              + New Schedule
            </CustomButton>
          </div>
        </div>

        {/* ================= RIGHT PANEL ================= */}
        <div className="border rounded-xl bg-white p-4 sm:p-5 space-y-5">
          {/* TOP ROW */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <input
              type="text"
              value={editingSchedule?.name || ""}
              onChange={(e) =>
                setEditingSchedule((p) => ({ ...p, name: e.target.value }))
              }
              placeholder="Schedule Name"
              className={`${inputBase} w-full flex-1`}
            />

            <div className="flex gap-2 self-end sm:self-auto">
              <CustomButton
                variant="secondary"
                className="border-gray-300 text-gray-500 hover:bg-gray-50"
                onClick={handleClearSchedule}
              >
                <FiXCircle size={18} />
              </CustomButton>

              <CustomButton
                variant="secondary"
                className="border-red-300 text-red-600 hover:bg-red-50"
                onClick={handleDeleteSchedule}
              >
                <FiTrash2 size={18} />
              </CustomButton>
            </div>
          </div>

          {/* WEEK DAYS */}
          <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2">
            {WEEK_DAYS.map((d) => (
              <CustomButton
                key={d}
                variant={
                  editingSchedule?.days?.includes(d) ? "primary" : "secondary"
                }
                onClick={() =>
                  setEditingSchedule((p) => ({
                    ...p,
                    days: p?.days?.includes(d)
                      ? p.days.filter((x) => x !== d)
                      : [...(p?.days || []), d],
                  }))
                }
                className="px-4 py-1.5"
              >
                {d}
              </CustomButton>
            ))}
          </div>

          {/* TIME INPUTS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="time"
              value={editingSchedule?.openTime || ""}
              onChange={(e) =>
                setEditingSchedule((p) => ({
                  ...p,
                  openTime: e.target.value,
                }))
              }
              className={timeInput}
            />

            <input
              type="time"
              value={editingSchedule?.closeTime || ""}
              onChange={(e) =>
                setEditingSchedule((p) => ({
                  ...p,
                  closeTime: e.target.value,
                }))
              }
              className={timeInput}
            />
          </div>

          {/* ACTION BUTTONS */}
          <div className="flex flex-col sm:flex-row sm:justify-end gap-3 pt-2">
            <CustomButton
              variant="secondary"
              className="w-full sm:w-auto"
              onClick={() => setEditingSchedule({ ...activeSchedule })}
            >
              Cancel
            </CustomButton>

            <CustomButton
              className="w-full sm:w-auto"
              onClick={handleSaveSchedule}
            >
              Save Schedule
            </CustomButton>
          </div>
        </div>
      </div>

      <div className="border rounded-xl p-4 bg-gray-50 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Booking Mode</p>
            <p className="text-xs text-gray-500 mt-1">
              <p className="text-xs text-gray-500 mt-2">
                {specificStartRequired
                  ? "Start times are generated based on the selected interval. Duration is determined by escalation rules."
                  : "Guests can book at any time within the operating window."}
              </p>
            </p>
          </div>

          <TogglePill
            options={[
              { label: "Flexible Start Times", value: "FLEXIBLE" },
              { label: "Specific Start Times", value: "FIXED" },
            ]}
            active={specificStartRequired ? "FIXED" : "FLEXIBLE"}
            onChange={(val) => {
              const isFixed = val === "FIXED";
              setSpecificStartRequired(isFixed);

              if (!isFixed) {
                setStartInterval(""); // clear interval when switching to flexible
              }
            }}
          />
        </div>
      </div>
      {specificStartRequired && (
        <div className="w-full xl:w-[260px] relative mt-4 mb-5">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Start Time Interval
          </label>

          <div className="relative">
            {/* BUTTON */}
            <button
              type="button"
              onClick={() => {
                if (!startInterval) {
                  setIntervalDropdownOpen((p) => !p);
                }
              }}
              className={`
    flex items-center gap-2
    w-full h-[44px]
    rounded-xl px-3
    text-sm font-medium
    transition shadow-sm
    border
    ${
      startInterval
        ? "bg-toucanTeal text-white border-toucanTeal"
        : "bg-white text-toucanTeal border-toucanTeal"
    }
    hover:opacity-90
  `}
            >
              <span className="flex-1 text-left truncate">
                {startInterval
                  ? intervalOptions.find((i) => i.value === startInterval)
                      ?.label
                  : "Select Interval"}
              </span>

              {/* 🔥 CONDITIONAL ICON */}
              {startInterval ? (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation(); // prevent dropdown toggle
                    setStartInterval("");
                    setIntervalDropdownOpen(false);
                  }}
                  className="hover:opacity-80"
                >
                  <FiXCircle size={18} />
                </button>
              ) : (
                <FiChevronDown
                  size={16}
                  className={`transition-transform ${
                    intervalDropdownOpen ? "rotate-180" : ""
                  }`}
                />
              )}
            </button>

            {/* DROPDOWN */}
            {intervalDropdownOpen && (
              <div
                className="
            absolute left-0 top-full mt-2
            w-full
            bg-white border border-gray-200
            rounded-lg shadow-xl
            py-2
            max-h-[260px] overflow-y-auto
            z-[9999]
          "
              >
                {intervalOptions.map((option) => {
                  const isSelected = startInterval === option.value;

                  return (
                    <div
                      key={option.value}
                      onClick={() => {
                        setStartInterval(option.value);
                        setIntervalDropdownOpen(false);
                      }}
                      className={`
                  px-4 py-2 text-sm cursor-pointer transition-colors
                  ${
                    isSelected
                      ? "bg-toucanTeal/10 text-toucanTeal font-medium"
                      : "text-gray-700 hover:bg-gray-100"
                  }
                `}
                    >
                      {option.label}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {specificStartRequired &&
        startInterval &&
        previewStartTimes.length > 0 && (
          <div className="border rounded-xl p-4 bg-white mt-4">
            <p className="text-sm font-medium mb-3">Available Start Times</p>

            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {previewStartTimes.map((time, idx) => (
                <div
                  key={idx}
                  className="text-xs px-2 py-1 bg-toucanTeal/10 text-toucanTeal rounded-md text-center"
                >
                  {time}
                </div>
              ))}
            </div>
          </div>
        )}

      {/* ================= EXISTING FLOW CONTINUES ================= */}

      {/* =====================================================
    SECTION 2 — APPLY SCHEDULE TO CALENDAR
   ===================================================== */}

      <h3 className="text-[15px] font-semibold text-gray-900 mb-3 mt-5">
        Apply Schedule to Calendar
      </h3>

      <div className="flex flex-col xl:flex-row gap-3 items-end mb-8">
        {/* SCHEDULE DROPDOWN */}
        <div className="w-full xl:w-[260px] relative">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Schedule
          </label>

          <div className="relative">
            {/* BUTTON */}
            <button
              type="button"
              onClick={() => setScheduleDropdownOpen((p) => !p)}
              className={`
        flex items-center gap-2
        w-full h-[44px]
        rounded-xl px-3
        text-sm font-medium
        transition shadow-sm
        border
        ${
          selectedSchedule
            ? "bg-toucanTeal text-white border-toucanTeal"
            : "bg-white text-toucanTeal border-toucanTeal"
        }
        hover:opacity-90
      `}
            >
              <FiFilter size={16} />

              <span className="flex-1 text-left truncate">
                {selectedSchedule ? selectedSchedule.name : "Select schedule"}
              </span>

              <FiChevronDown
                size={16}
                className={`transition-transform ${
                  scheduleDropdownOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {/* DROPDOWN */}
            {scheduleDropdownOpen && (
              <div
                className="
          absolute left-0 top-full mt-2
          w-full
          bg-white border border-gray-200
          rounded-lg shadow-xl
          py-2
          max-h-[260px] overflow-y-auto
          z-[9999]
        "
              >
                {schedules.map((s) => {
                  const isSelected = selectedScheduleId === s.id;

                  return (
                    <div
                      key={s.id}
                      onClick={() => {
                        handleSelectSchedule(s.id);
                        setScheduleDropdownOpen(false);
                      }}
                      className={`
                px-4 py-2 text-sm cursor-pointer transition-colors
                ${
                  isSelected
                    ? "bg-toucanTeal/10 text-toucanTeal font-medium"
                    : "text-gray-700 hover:bg-gray-100"
                }
              `}
                    >
                      {s.name} {s.isActive ? "(Active)" : ""}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* START DATE */}
        <div className="w-full xl:w-[180px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            Start Date
          </label>
          <input
            type="date"
            min={todayStr} // 🔥 THIS LINE
            value={rangeStart ? format(rangeStart, "yyyy-MM-dd") : ""}
            onChange={(e) =>
              setRangeStart(e.target.value ? new Date(e.target.value) : null)
            }
            className={inputBase}
          />
        </div>

        {/* END DATE */}
        <div className="w-full xl:w-[180px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">
            End Date
          </label>
          <input
            type="date"
            min={todayStr} // 🔥 THIS LINE
            value={rangeEnd ? format(rangeEnd, "yyyy-MM-dd") : ""}
            onChange={(e) =>
              setRangeEnd(e.target.value ? new Date(e.target.value) : null)
            }
            className={inputBase}
          />
        </div>

        {/* BUTTONS */}
        <div className="flex flex-wrap gap-3 items-center w-full">
          <CustomButton
            variant="secondary"
            onClick={handleAutoPopulateMonth}
            className="h-[44px]"
          >
            Auto Month
          </CustomButton>

          <CustomButton
            variant="secondary"
            onClick={handleAutoPopulateYear}
            className="h-[44px]"
          >
            Auto Year
          </CustomButton>

          <CustomButton
            variant="secondary"
            onClick={handleApplyToDateRange}
            className="h-[44px]"
          >
            Apply Date Range
          </CustomButton>

          <CustomButton
            variant="secondary"
            onClick={handleClearCalendar}
            title="Clear all calendar availability"
            className="
    h-[44px] w-[44px]
    flex items-center justify-center
    ml-auto
    border border-red-300
    text-red-600
    hover:bg-red-50 hover:border-red-400
    focus:ring-2 focus:ring-red-300
  "
          >
            <FiTrash2 size={16} />
          </CustomButton>
        </div>
      </div>

      {/* APPLY TO DATE RANGE — 🔥 RESTORED */}

      <UnifiedCalendar
        mode="range"
        availabilityMap={calendarAvailability}
        onRangeSelect={handleCalendarClick}
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        allowedWeekDays={openDays}
        disabled={!isScheduleValid}
        selectedDate={selectedDate}
      />
      {/* ================= CALENDAR LEGEND ================= */}
      <div className="mt-5 p-3 bg-white">
        <div className="flex flex-wrap gap-8 text-sm">
          {/* FULL CLOSE */}
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full"
              style={{ background: "#DC2626" }}
            ></span>
            <span className="text-gray-700">Full Day Closed</span>
          </div>

          {/* CUSTOM HOURS */}
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full"
              style={{ background: "#F59E0B" }}
            ></span>
            <span className="text-gray-700">Custom Hours</span>
          </div>

          {/* CLOSURE WINDOWS */}
          <div className="flex items-center gap-2">
            <span
              className="h-3 w-3 rounded-full"
              style={{ background: "#7C3AED" }}
            ></span>
            <span className="text-gray-700">Blocked Time Range</span>
          </div>
        </div>
      </div>

      {/* ================= OVERRIDE PANEL (RESTORED) ================= */}

      {selectedDate && (
        <div className="mt-6 rounded-2xl border shadow-sm bg-white max-w-full">
          {/* HEADER */}
          <div className="flex items-center justify-between px-5 py-4 border-b">
            <span className="inline-flex items-center bg-toucanTeal/10 text-toucanTeal px-3 py-1 rounded-full text-sm font-medium">
              {selectedDate}
            </span>

            {existingOverride && (
              <CustomButton
                variant="secondary"
                className="text-red-600 border-red-500 hover:bg-red-50"
                onClick={handleRemoveOverride}
              >
                <FiTrash2 size={18} />
              </CustomButton>
            )}
          </div>

          {/* BODY */}
          <div className="p-5 space-y-5">
            {/* OVERRIDE TYPES */}
            <div className="space-y-3">
              {["FULL_CLOSE", "CUSTOM_HOURS", "CLOSURE_WINDOWS"].map((t) => (
                <label
                  key={t}
                  className="flex items-start gap-3 text-sm cursor-pointer"
                >
                  <input
                    type="radio"
                    checked={overrideType === t}
                    onChange={() => setOverrideType(t)}
                    className="mt-1 accent-toucanTeal"
                  />
                  <div>
                    <div className="font-medium text-gray-900">
                      {t === "FULL_CLOSE"
                        ? "Full Day Closed"
                        : t === "CUSTOM_HOURS"
                          ? "Custom Hours"
                          : "Add Closure Windows"}
                    </div>
                    <div className="text-xs text-gray-500">
                      {t === "FULL_CLOSE"
                        ? "Completely close this day"
                        : t === "CUSTOM_HOURS"
                          ? "Override opening and closing time"
                          : "Block specific time ranges during the day"}
                    </div>
                  </div>
                </label>
              ))}
            </div>

            {/* CUSTOM HOURS */}
            {overrideType === "CUSTOM_HOURS" && (
              <div className="flex gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">Start</span>
                  <input
                    type="time"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className={timeInput}
                  />
                </div>

                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500">End</span>
                  <input
                    type="time"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className={timeInput}
                  />
                </div>
              </div>
            )}

            {/* CLOSURE WINDOWS */}
            {overrideType === "CLOSURE_WINDOWS" && (
              <div className="space-y-3">
                <CustomButton
                  variant="secondary"
                  onClick={handleAddWindow}
                  className="w-fit"
                >
                  + Add Window
                </CustomButton>

                {closures.map((w, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 border rounded-xl bg-gray-50"
                  >
                    <input
                      type="time"
                      value={w.start}
                      className={timeInput}
                      onChange={(e) =>
                        setClosures((p) =>
                          p.map((x, idx) =>
                            idx === i ? { ...x, start: e.target.value } : x,
                          ),
                        )
                      }
                    />
                    <span className="text-gray-500">—</span>
                    <input
                      type="time"
                      value={w.end}
                      className={timeInput}
                      onChange={(e) =>
                        setClosures((p) =>
                          p.map((x, idx) =>
                            idx === i ? { ...x, end: e.target.value } : x,
                          ),
                        )
                      }
                    />
                    <button
                      className="ml-auto text-red-500 hover:text-red-700"
                      onClick={() =>
                        setClosures((p) => p.filter((_, idx) => idx !== i))
                      }
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* FOOTER */}
          <div className="flex justify-end px-5 py-4 border-t">
            <CustomButton onClick={handleSaveOverride}>
              {existingOverride ? "Update Override" : "Save Override"}
            </CustomButton>
          </div>
        </div>
      )}

      <hr className="my-8" />

      <h3 className="text-[15px] font-semibold text-gray-900 mb-3">
        Escalation Rules
      </h3>

      <p className="text-xs text-gray-600 mb-4">
        Define actions when guest limits exceed allowed ranges.
      </p>

      {escalationRules.map((rule, index) => {
        const prevRule = escalationRules[index - 1] || {};
        const isOpen = openEscalationRows.includes(index);

        return (
          <div
            key={index}
            className="w-full border border-gray-200 rounded-2xl bg-white shadow-sm mb-3"
          >
            {/* HEADER */}
            <button
              type="button"
              onClick={() =>
                setOpenEscalationRows((prev) =>
                  prev.includes(index)
                    ? prev.filter((i) => i !== index)
                    : [...prev, index],
                )
              }
              className="
          w-full flex justify-between items-center
          px-5 py-4 text-left
          hover:bg-gray-50
          rounded-2xl
        "
            >
              <div className="text-sm font-semibold text-gray-900">
                Guests {rule.minGuests || "?"} – {rule.maxGuests || "?"}
              </div>

              <span
                className={`
            text-gray-500 text-sm
            transition-transform duration-300
            ${isOpen ? "-rotate-90" : "rotate-90"}
          `}
              >
                &gt;
              </span>
            </button>

            {/* BODY */}
            <div
              className={`
          overflow-hidden transition-all duration-300 ease-in-out
          ${isOpen ? "max-h-[320px] opacity-100" : "max-h-0 opacity-0"}
        `}
            >
              <div
                className="
            px-5 pb-5 pt-1
            grid grid-cols-[1fr_1fr_1fr_auto]
            gap-3 items-end
          "
              >
                {/* MIN */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Min Guests
                  </label>
                  <input
                    type="number"
                    min={index > 0 ? Number(prevRule.maxGuests || 0) + 1 : 1}
                    value={rule.minGuests}
                    onChange={(e) =>
                      setEscalationRules((prev) =>
                        prev.map((r, i) =>
                          i === index ? { ...r, minGuests: e.target.value } : r,
                        ),
                      )
                    }
                    className="
                w-full h-10
                rounded-lg border border-gray-300
                px-3 text-sm
                focus:ring-1 focus:ring-toucanTeal
              "
                  />
                </div>

                {/* MAX */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Max Guests
                  </label>
                  <input
                    type="number"
                    min={rule.minGuests || 1}
                    value={rule.maxGuests}
                    onChange={(e) =>
                      setEscalationRules((prev) =>
                        prev.map((r, i) =>
                          i === index ? { ...r, maxGuests: e.target.value } : r,
                        ),
                      )
                    }
                    className="
                w-full h-10
                rounded-lg border border-gray-300
                px-3 text-sm
                focus:ring-1 focus:ring-toucanTeal
              "
                  />
                </div>

                {/* DURATION */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">
                    Duration (min)
                  </label>
                  <input
                    type="number"
                    value={rule.duration}
                    onChange={(e) =>
                      setEscalationRules((prev) =>
                        prev.map((r, i) =>
                          i === index ? { ...r, duration: e.target.value } : r,
                        ),
                      )
                    }
                    className="
                w-full h-10
                rounded-lg border border-gray-300
                px-3 text-sm
                focus:ring-1 focus:ring-toucanTeal
              "
                  />
                </div>

                {/* REMOVE */}
                <button
                  type="button"
                  onClick={() =>
                    setEscalationRules((prev) =>
                      prev.filter((_, i) => i !== index),
                    )
                  }
                  className="
              h-10 w-10
              flex items-center justify-center
              rounded-lg
              border border-red-200
              text-red-500
              hover:bg-red-50
            "
                  title="Remove rule"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        );
      })}

      <CustomButton
        variant="secondary"
        onClick={handleAddEscalationRule}
        className="mt-2"
      >
        + Add Escalation Rule
      </CustomButton>

      <div className="flex justify-between mt-8">
        <CustomButton variant="secondary" onClick={onBack}>
          Back
        </CustomButton>
        <CustomButton
          onClick={() => {
            if (specificStartRequired && !startInterval) {
              toast.error("Please select start time interval");
              return;
            }
            if (!validateEscalationRules()) return;

            onNext({
              schedules, // 🔥 ADD THIS
              escalationRules,
              calendarAvailability,
              rangeStart,
              rangeEnd,
              specificStartRequired,
              startInterval,
            });
          }}
        >
          Continue
        </CustomButton>
      </div>
    </div>
  );
}
